import { useCallback, useState } from 'react';
import { Loader2, Play, ClipboardCopy, ReceiptText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { adminWiseSupplierAccrecCensusAPI } from '../../lib/adminWiseSupplierAccrecCensusApi';
import { useJobPolling, useJobResume, JobErrorBlock } from './JobPollingPanel';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const CLASSIFICATION_LABELS = {
  intercompany: 'intercompany',
  refund_of_paid_invoice: 'refund of a paid invoice',
  duplicate_payment_return: 'duplicate payment return',
  unclassified_needs_review: 'unclassified, needs review',
};

function xeroPaymentStateText(state) {
  if (!state) return '-';
  const parts = [state.payment_status || 'unknown'];
  if (state.xero_status) parts.push(`(${state.xero_status})`);
  if (state.xero_paid_date) parts.push(`paid ${state.xero_paid_date}`);
  return parts.join(' ');
}

function rowToLine(r) {
  return [
    r.transaction_id, r.date, `${r.amount ?? ''} ${r.currency ?? ''}`.trim(), r.counterparty,
    r.reference, r.invoice_number, xeroPaymentStateText(r.xero_payment_state),
    r.signals?.invoice_paid_in_xero ? 'paid_in_xero' : 'not_paid_in_xero',
    r.signals?.counterparty_resolves_to_own_entity_id || 'no_entity_match',
    `other_hits=${r.signals?.other_transactions_reference_hitting_same_invoice_number ?? 0}`,
    r.signals?.credit_note_check,
    r.suggested_classification,
  ].join('\t');
}

function copyRows(rows) {
  if (!rows.length) return;
  const header = [
    'transaction_id', 'date', 'amount', 'counterparty', 'reference', 'invoice_number',
    'xero_payment_state', 'paid_in_xero', 'intercompany_entity', 'other_reference_hits',
    'credit_note_check', 'suggested_classification',
  ].join('\t');
  const text = [header, ...rows.map(rowToLine)].join('\n');
  navigator.clipboard.writeText(text)
    .then(() => toast.success(`Copied ${rows.length} row(s)`))
    .catch((err) => toast.error('Could not copy to clipboard: ' + getErrorMessage(err)));
}

// Reports every Wise Supplier cohort transaction whose reference hits one
// of OUR OWN Xero ACCREC (sales) invoices instead of an ACCPAY supplier
// bill -- a collision the real matcher's invoice_pool never surfaces
// (build_candidate_invoice_pool never includes ACCREC invoices). Read-only:
// never calls the matcher, never writes, never reconciles anything. See
// services/wise_supplier_reconciliation.py::census_accrec_reference_hits.
export function AccrecOutflowCensusCard() {
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(null);

  const poll = useJobPolling({
    fetchStatus: adminWiseSupplierAccrecCensusAPI.jobStatus,
    onCompleted: useCallback((res) => {
      setResult(res);
      setFailed(null);
      toast.success('ACCREC outflow census complete');
    }, []),
    onFailed: useCallback((error) => {
      setFailed(error);
      toast.error(error ?? 'ACCREC outflow census failed');
    }, []),
  });

  useJobResume({ fetchLatest: adminWiseSupplierAccrecCensusAPI.jobLatest, poll });

  const run = async () => {
    setStarting(true);
    setFailed(null);
    try {
      const { data } = await adminWiseSupplierAccrecCensusAPI.start();
      if (data.job_id) {
        poll.start(data.job_id);
      } else if (data.status === 'already_running') {
        toast.error('A census run is already in progress.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const rows = result?.rows || [];
  const busy = starting || poll.polling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ReceiptText className="h-4 w-4 text-muted-foreground" />
          ACCREC Outflow Census
        </CardTitle>
        <CardDescription>
          Read-only. Within the current Wise Supplier cohort, reports every outgoing transaction
          whose reference hits one of our own Xero ACCREC (sales) invoices instead of an ACCPAY
          supplier bill -- a collision the real matcher never surfaces since its candidate pool
          never includes ACCREC invoices. Never writes, never reconciles, never calls the matcher.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="accrec-outflow-census-run"
            size="sm" onClick={run} disabled={busy}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
            Run census
          </Button>
          {rows.length > 0 && (
            <Button
              data-testid="accrec-outflow-census-copy"
              size="sm" variant="outline" onClick={() => copyRows(rows)}
            >
              <ClipboardCopy className="h-4 w-4 mr-1" /> Copy rows
            </Button>
          )}
        </div>

        <JobErrorBlock error={failed} testId="accrec-outflow-census-error" />

        {result && (
          <div className="space-y-2 text-xs" data-testid="accrec-outflow-census-result">
            <div className="flex flex-wrap gap-3">
              <span>Cohort: <strong>{fmtInt(result.cohort_count)}</strong></span>
              <span>ACCREC invoice pool: <strong>{fmtInt(result.accrec_invoice_pool_count)}</strong></span>
              <span className={result.match_count > 0 ? 'text-amber-600' : 'text-green-600'}>
                Hits: <strong>{fmtInt(result.match_count)}</strong>
              </span>
            </div>

            {rows.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="accrec-outflow-census-empty">
                No cohort transaction referenced one of our own ACCREC invoices.
              </p>
            ) : (
              <div className="border rounded overflow-auto max-h-96">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50 sticky top-0">
                      <th className="text-left font-medium px-2 py-1">Transaction</th>
                      <th className="text-left font-medium px-2 py-1">Date</th>
                      <th className="text-left font-medium px-2 py-1">Amount</th>
                      <th className="text-left font-medium px-2 py-1">Counterparty</th>
                      <th className="text-left font-medium px-2 py-1">Reference</th>
                      <th className="text-left font-medium px-2 py-1">Invoice #</th>
                      <th className="text-left font-medium px-2 py-1">Xero payment state</th>
                      <th className="text-left font-medium px-2 py-1">Signals</th>
                      <th className="text-left font-medium px-2 py-1">Classification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={`${r.transaction_id}_${r.invoice_id}`}
                        className="border-b last:border-b-0"
                        data-testid={`accrec-outflow-census-row-${r.transaction_id}`}
                      >
                        <td className="px-2 py-1 font-mono">{r.transaction_id}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{r.date || '-'}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{r.amount} {r.currency}</td>
                        <td className="px-2 py-1">{r.counterparty || '-'}</td>
                        <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]" title={r.reference}>
                          {r.reference || '-'}
                        </td>
                        <td className="px-2 py-1 font-mono">{r.invoice_number || '-'}</td>
                        <td className="px-2 py-1">{xeroPaymentStateText(r.xero_payment_state)}</td>
                        <td className="px-2 py-1">
                          <div className="flex flex-wrap gap-1">
                            <Badge variant={r.signals?.invoice_paid_in_xero ? 'default' : 'secondary'} className="text-[10px]">
                              {r.signals?.invoice_paid_in_xero ? 'paid in Xero' : 'not paid in Xero'}
                            </Badge>
                            {r.signals?.counterparty_resolves_to_own_entity_id && (
                              <Badge variant="destructive" className="text-[10px]">
                                intercompany: {r.signals.counterparty_resolves_to_own_entity_id}
                              </Badge>
                            )}
                            {r.signals?.other_transactions_reference_hitting_same_invoice_number > 0 && (
                              <Badge variant="outline" className="text-[10px]">
                                {r.signals.other_transactions_reference_hitting_same_invoice_number} other hit(s)
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[10px]" title="Xero CreditNotes are not ingested by this codebase">
                              credit note: {r.signals?.credit_note_check === 'undeterminable_no_credit_note_ingestion' ? 'undeterminable' : (r.signals?.credit_note_check || '-')}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          {CLASSIFICATION_LABELS[r.suggested_classification] || r.suggested_classification}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
